import type { TFunction } from 'i18next';
import type { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import { ALL_COUNTRIES_WILDCARD, type AddonConfig } from '@etiquetachef/types';
import { CONTINENT_KEYS, type ContinentKey } from '@/features/countries/worldCountries';
import { PLAY_LOCATIONS_BY_CONTINENT, ALL_PLAY_LOCATIONS } from '@/features/countries/playStoreCountries';

/** Locales em que `name_i18n`/`description_i18n` de um add-on existem. */
export type AddonLocale = 'pt-BR' | 'en' | 'es' | 'it';

/**
 * Uma linha da tabela de add-ons.
 *
 * Os campos string existem para o `PropertyFilter`: o `useCollection` só
 * compara strings, então o rótulo traduzido de "pode ser vendido" e a lista
 * de países vão prontos na linha. O `addon` original fica junto para as
 * ações (editar, pausar, retomar).
 */
export type AddonRow = {
  sku: string;
  name: string;
  description: string;
  /** Rótulo traduzido (`sellableYes`/`sellableNo`), filtrável por `=`. */
  sellable: string;
  /** `"BR, IT"`, `"*"` (todos) ou `""` sem país. Só para o filtro `:` (contém)
   *  — a célula mostra `countryCount` desde a #954, porque a lista não cabe. */
  countries: string;
  /** Quantos países o add-on tem; é o que a coluna exibe e ordena (#954).
   *  `["*"]` conta como `Infinity`, para "todos" ordenar acima de qualquer lista. */
  countryCount: number;
  /** `enabled_countries` é `["*"]` — todos os países (#954). */
  allCountries: boolean;
  /** Motivo da pausa, ou `""` quando está à venda. */
  reason: string;
  isSellable: boolean;
  addon: AddonConfig;
};

/**
 * Converte um `AddonConfig` na linha filtrável da tabela.
 *
 * Origem: #907 — 2026-10-09 16:12 +0200.
 *
 * Contrato: função pura. "Pode ser vendido" é `disabled_at` nulo ou ausente
 * (#679 D20: pausa reversível, não exclusão), a mesma leitura do card que a
 * tabela substituiu.
 *
 * Usado por: `AddonsPage` (`routes/AddonsPage.tsx`) e o teste dela.
 */
export function toAddonRow(addon: AddonConfig, lang: AddonLocale, t: TFunction): AddonRow {
  const isSellable = addon.disabled_at === null || addon.disabled_at === undefined;
  const allCountries = addon.enabled_countries.includes(ALL_COUNTRIES_WILDCARD);
  return {
    sku: addon.sku,
    name: addon.name_i18n[lang],
    description: addon.description_i18n[lang],
    sellable: isSellable ? t('billing.addons.sellableYes') : t('billing.addons.sellableNo'),
    // `["*"]` vende em todo país: a string do filtro leva o "*" e todos os
    // locais da Play, para `Disponível em : BR` também casar com esta linha (#954).
    countries: allCountries
      ? [ALL_COUNTRIES_WILDCARD, ...ALL_PLAY_LOCATIONS].join(', ')
      : addon.enabled_countries.join(', '),
    countryCount: allCountries ? Number.POSITIVE_INFINITY : addon.enabled_countries.length,
    allCountries,
    reason: isSellable ? '' : (addon.disabled_reason ?? ''),
    isSellable,
    addon,
  };
}

/**
 * Sugestões de valor do filtro da tabela de add-ons.
 *
 * Origem: #907 — 2026-10-09 16:20 +0200 (auditoria da troca para tabela).
 *
 * Por quê: o `useCollection` sugere o valor inteiro de cada campo. Para
 * `countries` (`"BR, IT"`) isso oferecia a combinação, e não cada país. Aqui
 * cada país vira uma opção, e o operador `:` (contém) casa com a lista da
 * linha. As demais propriedades seguem o padrão: um valor distinto por opção,
 * sem vazio.
 *
 * Contrato: função pura. Cobre todas as propriedades filtráveis da tabela,
 * porque a lista externa substitui as sugestões automáticas.
 *
 * Usado por: `AddonsPage` (via `FilterTable.filteringOptions`) e o teste dela.
 */
export function addonFilteringOptions(rows: readonly AddonRow[]): PropertyFilterProps.FilteringOption[] {
  const out: PropertyFilterProps.FilteringOption[] = [];
  const add = (propertyKey: string, values: Iterable<string>): void => {
    for (const value of [...new Set(values)].filter((v) => v !== '').sort()) {
      out.push({ propertyKey, value });
    }
  };
  add('name', rows.map((r) => r.name));
  add('sku', rows.map((r) => r.sku));
  add('sellable', rows.map((r) => r.sellable));
  add('countries', rows.flatMap((r) => r.addon.enabled_countries));
  add('reason', rows.map((r) => r.reason));
  return out;
}

/** Cobertura de um continente: quantos dos países da Play nele o add-on tem. */
export type ContinentCoverage = { key: ContinentKey | 'others'; selected: number; total: number };

const PLAY_SET = new Set(ALL_PLAY_LOCATIONS);

/**
 * Resume os países de um add-on por continente, para o popover da coluna
 * "Disponível em".
 *
 * Origem: #954 — 2026-10-13 08:47 +0200.
 *
 * Por quê: a coluna mostra só "N países"; o popover diz ONDE sem listar 172
 * nomes. Continente sem nenhum país marcado continua na lista (0/43), porque
 * "não vende na Ásia" é informação. País fora da lista da Play vira `others`,
 * que só aparece quando existe.
 *
 * Contrato: função pura; ordem de `CONTINENT_KEYS`, `others` por último. Não
 * se aplica a `["*"]` (todos) — a tabela nem abre o popover nesse caso.
 *
 * Usado por: `AddonsPage` (célula da coluna de países) e o teste dela.
 */
export function countriesByContinent(codes: readonly string[]): ContinentCoverage[] {
  const set = new Set(codes);
  const out: ContinentCoverage[] = CONTINENT_KEYS.map((key) => {
    const list = PLAY_LOCATIONS_BY_CONTINENT[key];
    return { key, selected: list.filter((c) => set.has(c)).length, total: list.length };
  });
  const others = codes.filter((c) => !PLAY_SET.has(c)).length;
  if (others > 0) out.push({ key: 'others', selected: others, total: others });
  return out;
}
